import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trash2, ShoppingBag, ArrowRight, ChevronRight, Tag, CheckCircle2, XCircle, Mail, FileCheck } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { useCart } from "@/lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUPON_KEY = "tcsw_coupon_v1";
const CAPTURE_KEY = "tcsw_cart_capture_email";

/**
 * Cart — adjust qty/remove, apply coupon, opt-in for abandoned-cart nudge, checkout.
 * Coupon is stored in localStorage and re-priced by the server at checkout — the frontend
 * only shows a preview using the same math the backend applies.
 */
export default function Cart() {
  const { items, removeItem, updateQuantity, clear, subtotal, itemCount } = useCart();
  const [coupon, setCoupon] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COUPON_KEY) || "null"); } catch { return null; }
  });
  const [couponInput, setCouponInput] = useState("");
  const [validating, setValidating] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem(CAPTURE_KEY) || "");
  const [captureStatus, setCaptureStatus] = useState(null); // null | "saved"
  const captureTimer = useRef(null);

  useSEO({
    title: "Cart",
    description: "Review your custom print order — Toronto Custom Signs & Wraps.",
    path: "/cart",
  });

  // Persist coupon selection
  useEffect(() => {
    if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    else localStorage.removeItem(COUPON_KEY);
  }, [coupon]);

  const discount = useMemo(() => {
    if (!coupon || !subtotal) return 0;
    if (coupon.kind === "percent") return Math.min(subtotal, subtotal * (Number(coupon.value) || 0) / 100);
    return Math.min(subtotal, Number(coupon.value) || 0);
  }, [coupon, subtotal]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/checkout/coupons/validate`, { code });
      if (data.valid) {
        setCoupon({ code: data.code, kind: data.kind, value: data.value });
        setCouponInput("");
        toast.success(`Coupon "${data.code}" applied.`);
      } else {
        toast.error("That coupon isn't valid or has expired.");
      }
    } catch {
      toast.error("Could not validate coupon. Try again.");
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    toast.message("Coupon removed.");
  };

  // Debounced abandoned-cart capture — fires 1.2s after buyer stops typing an email.
  useEffect(() => {
    if (captureTimer.current) clearTimeout(captureTimer.current);
    setCaptureStatus(null);
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@") || trimmed.length < 5 || items.length === 0) return;
    captureTimer.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/checkout/cart-capture`, {
          email: trimmed,
          lines: items.map((it) => ({
            product_id: it.product_id,
            product_name: it.product_name,
            option_ids: it.option_ids,
            quantity: it.quantity,
            unit_price: it.unit_price,
          })),
        });
        localStorage.setItem(CAPTURE_KEY, trimmed);
        setCaptureStatus("saved");
      } catch {
        /* silent — this is opt-in, don't disrupt UX */
      }
    }, 1200);
    return () => { if (captureTimer.current) clearTimeout(captureTimer.current); };
  }, [email, items]);

  const estimatedTotal = Math.max(0, subtotal - discount);

  return (
    <div className="pt-28 pb-24 bg-ink-950 min-h-screen" data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <nav className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-4 flex items-center gap-2">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={11} />
          <Link to="/shop" className="hover:text-gold">Print Shop</Link>
          <ChevronRight size={11} />
          <span className="text-gold">Cart</span>
        </nav>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow gold-bar">Your order</div>
            <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight" data-testid="cart-title">Cart</h1>
            <p className="text-white/50 text-sm mt-3">{itemCount} item{itemCount !== 1 ? "s" : ""} in your cart</p>
          </div>
          {items.length > 0 && (
            <button
              onClick={() => { if (window.confirm("Empty the cart?")) clear(); }}
              data-testid="cart-clear"
              className="text-[11px] uppercase tracking-[0.22em] text-white/50 hover:text-red-400 transition-colors"
            >Clear cart</button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="card-dark p-16 text-center" data-testid="cart-empty">
            <ShoppingBag size={32} className="mx-auto text-gold mb-4 opacity-50" />
            <div className="font-display text-2xl">Your cart is empty</div>
            <p className="text-white/50 text-sm mt-3">Browse the shop and add some print products to get started.</p>
            <Link to="/shop" className="btn-gold inline-flex mt-8" data-testid="cart-shop-link">Browse the shop</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,380px)] gap-8">
            {/* Line items */}
            <ul className="space-y-4">
              {items.map((it) => (
                <li key={it.uid} className="card-dark p-6" data-testid={`cart-item-${it.uid}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{it.category}</div>
                      <Link to={`/shop/product/${it.product_id}`} className="font-display text-xl mt-1 block hover:text-gold transition-colors">{it.product_name}</Link>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">#{it.product_id} · {it.sku}</div>
                    </div>
                    <button
                      onClick={() => removeItem(it.uid)}
                      data-testid={`remove-item-${it.uid}`}
                      className="text-white/40 hover:text-red-400 transition-colors p-2"
                      aria-label="Remove item"
                    ><Trash2 size={14} /></button>
                  </div>

                  <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    {Object.entries(it.selection || {}).map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-[9px] uppercase tracking-[0.22em] text-white/40">{k}</dt>
                        <dd className="text-white/85 mt-0.5">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>

                  {it.artwork_token && (
                    <div
                      className="mt-4 flex items-center gap-2 px-3 py-2 border border-emerald-500/40 bg-emerald-500/5 rounded-sm text-xs"
                      data-testid={`cart-artwork-${it.uid}`}
                    >
                      <FileCheck size={13} className="text-emerald-400 shrink-0" />
                      <span className="text-emerald-300 truncate">Artwork attached: {it.artwork_filename}</span>
                    </div>
                  )}

                  <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <label className="text-[10px] uppercase tracking-[0.22em] text-white/50">Sets</label>
                      <input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) => updateQuantity(it.uid, e.target.value)}
                        data-testid={`cart-qty-${it.uid}`}
                        className="w-20 bg-ink-800 border border-white/10 text-white px-3 py-2 text-sm rounded-sm outline-none focus:border-gold"
                      />
                      <span className="text-white/40 text-xs">× ${Number(it.unit_price).toFixed(2)}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Line total</div>
                      <div className="font-display text-xl text-gold" data-testid={`line-total-${it.uid}`}>
                        ${(Number(it.unit_price) * Number(it.quantity)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <aside className="lg:sticky lg:top-24 self-start space-y-6">
              <div className="card-dark p-7" data-testid="cart-summary">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Order summary</div>

                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Items</span>
                    <span className="text-white">{itemCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Subtotal</span>
                    <span className="text-white" data-testid="cart-subtotal">${subtotal.toFixed(2)} CAD</span>
                  </div>
                  {coupon && (
                    <div className="flex justify-between text-sm" data-testid="cart-discount-row">
                      <span className="text-gold flex items-center gap-1.5">
                        <Tag size={11} /> {coupon.code}
                      </span>
                      <span className="text-gold">− ${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-white/40">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/40">
                    <span>Taxes</span>
                    <span>Calculated at checkout</span>
                  </div>
                </div>

                {/* Coupon input */}
                <div className="mt-6 pt-5 border-t border-white/5">
                  {coupon ? (
                    <div className="flex items-center justify-between gap-3 border border-gold/40 bg-gold/5 p-3 rounded-sm" data-testid="cart-coupon-applied">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 size={14} className="text-gold" />
                        <div>
                          <div className="text-white/85 font-mono text-[13px]">{coupon.code}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                            {coupon.kind === "percent" ? `${coupon.value}% off` : `$${Number(coupon.value).toFixed(2)} off`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={removeCoupon}
                        data-testid="cart-coupon-remove"
                        className="text-white/50 hover:text-red-400 transition-colors"
                        aria-label="Remove coupon"
                      >
                        <XCircle size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                        placeholder="Coupon code"
                        data-testid="cart-coupon-input"
                        className="flex-1 bg-ink-800 border border-white/10 text-white px-3 py-2.5 text-sm rounded-sm outline-none focus:border-gold font-mono uppercase"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={!couponInput.trim() || validating}
                        data-testid="cart-coupon-apply"
                        className="text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 border border-gold text-gold hover:bg-gold hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {validating ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-5 border-t border-white/5 flex justify-between items-baseline">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-white/60">Estimated total</span>
                  <span className="font-display text-3xl text-gold" data-testid="cart-estimated-total">${estimatedTotal.toFixed(2)}</span>
                </div>

                <Link
                  to="/checkout"
                  data-testid="cart-checkout-btn"
                  className="btn-gold w-full justify-center mt-6"
                >
                  Checkout <ArrowRight size={14} />
                </Link>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 text-center mt-3">
                  Secure payment · Powered by Square
                </p>

                <Link to="/shop" className="btn-ghost w-full justify-center mt-4 !text-[11px]" data-testid="continue-shopping">
                  ← Continue shopping
                </Link>
              </div>

              {/* Abandoned-cart email opt-in */}
              <div className="card-dark p-6" data-testid="cart-email-capture">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
                  <Mail size={11} className="text-gold" /> Save your cart
                </div>
                <p className="text-white/60 text-xs mt-2 leading-relaxed">
                  Drop your email and we&apos;ll save this cart so you can finish later — or nudge you if you forget.
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="cart-email-input"
                  className="w-full mt-3 bg-ink-800 border border-white/10 text-white px-3 py-2.5 text-sm rounded-sm outline-none focus:border-gold"
                />
                {captureStatus === "saved" && (
                  <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-1.5" data-testid="cart-email-saved">
                    <CheckCircle2 size={10} /> Saved
                  </div>
                )}
              </div>

              <div className="text-[11px] text-white/40 leading-relaxed">
                Need a manual quote or have a custom project?{" "}
                <Link to="/contact" className="text-gold hover:underline">Contact us</Link> — our team is happy to help.
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
